import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Kasbly typography — the design-system type scale as components.
 *
 * Sizes/line-heights come from the @theme tokens in globals.css
 * (--text-h1 … --text-overline); heading weight/tracking/balance come
 * from the base layer, which also resets letter-spacing for Arabic
 * (Arabic never takes negative tracking). Logical properties only —
 * the site serves an RTL locale.
 *
 * Judgment rules live in the design-system doc: one gold word per
 * headline max; bold the outcome, not the feature.
 */

function TypographyH1({ className, ...props }: React.ComponentProps<'h1'>) {
  return (
    <h1 data-slot="typography-h1" className={cn('scroll-m-20 text-h1', className)} {...props} />
  );
}

function TypographyH2({ className, ...props }: React.ComponentProps<'h2'>) {
  return (
    <h2
      data-slot="typography-h2"
      className={cn('scroll-m-20 text-h2 first:mt-0', className)}
      {...props}
    />
  );
}

function TypographyH3({ className, ...props }: React.ComponentProps<'h3'>) {
  return (
    <h3 data-slot="typography-h3" className={cn('scroll-m-20 text-h3', className)} {...props} />
  );
}

function TypographyH4({ className, ...props }: React.ComponentProps<'h4'>) {
  return (
    <h4 data-slot="typography-h4" className={cn('scroll-m-20 text-h4', className)} {...props} />
  );
}

function TypographyP({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="typography-p"
      className={cn('text-body [&:not(:first-child)]:mt-6', className)}
      {...props}
    />
  );
}

function TypographyLead({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="typography-lead"
      className={cn('text-body-lg text-muted-foreground', className)}
      {...props}
    />
  );
}

function TypographyBlockquote({ className, ...props }: React.ComponentProps<'blockquote'>) {
  return (
    <blockquote
      data-slot="typography-blockquote"
      className={cn('mt-6 border-s-2 ps-6 italic', className)}
      {...props}
    />
  );
}

function TypographyList({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="typography-list"
      className={cn('my-6 ms-6 list-disc [&>li]:mt-2', className)}
      {...props}
    />
  );
}

function TypographyInlineCode({ className, ...props }: React.ComponentProps<'code'>) {
  return (
    <code
      data-slot="typography-inline-code"
      className={cn(
        'bg-muted relative rounded-sm px-[0.3rem] py-[0.2rem] font-mono text-small font-semibold',
        className,
      )}
      {...props}
    />
  );
}

function TypographyLarge({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="typography-large"
      className={cn('text-body-lg font-medium', className)}
      {...props}
    />
  );
}

function TypographySmall({ className, ...props }: React.ComponentProps<'small'>) {
  return (
    <small
      data-slot="typography-small"
      className={cn('text-small font-medium', className)}
      {...props}
    />
  );
}

function TypographyMuted({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="typography-muted"
      className={cn('text-small text-muted-foreground', className)}
      {...props}
    />
  );
}

function TypographyCaption({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="typography-caption"
      className={cn('text-caption font-medium text-muted-foreground', className)}
      {...props}
    />
  );
}

/** Section kicker — small caps, +12% tracking (.text-overline utility). */
function TypographyOverline({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p data-slot="typography-overline" className={cn('text-overline', className)} {...props} />
  );
}

export {
  TypographyH1,
  TypographyH2,
  TypographyH3,
  TypographyH4,
  TypographyP,
  TypographyLead,
  TypographyBlockquote,
  TypographyList,
  TypographyInlineCode,
  TypographyLarge,
  TypographySmall,
  TypographyMuted,
  TypographyCaption,
  TypographyOverline,
};
